// ═══════════════════════════════════════════════════════════════
// GIVING REPORT
// Path: src/components/reports/GivingReport.jsx
//
// Two-section report:
//   1. Summary — KPI tiles + fund breakdown for date range
//   2. Detail — gifts by fund, sorted by month
//
// Self-contained — fetches its own data from gifts table.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { DollarSign, Calendar, TrendingUp, Printer, Filter } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { PAYMENT_METHODS } from "../../constants/giftTypes";
import { Card } from "../ui/Card";
import { ActionButton } from "../ui/ActionButton";
import { supabase } from "../../lib/supabase";
import { getFiscalDates } from "../../utils/fiscalYear";

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);

const fmtCurrencyFull = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount || 0);

const getTypeLabel = (method) =>
  PAYMENT_METHODS.find((m) => m.id === method)?.label || method || "Other";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const fmtMonth = (dateStr) => {
  const d = new Date(dateStr + "T12:00:00");
  return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
};

const fmtMonthKey = (dateStr) => dateStr.slice(0, 7); // YYYY-MM

export const GivingReport = () => {
  const { palette, chapterSettings, dataVersion } = useApp();
  const fiscalStart = chapterSettings?.fiscalYearStartMonth || 1;
  const fiscal = getFiscalDates(fiscalStart);

  // Date range state
  const [dateFrom, setDateFrom] = useState(fiscal.yearStart);
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fund filter state
  const [selectedFundIds, setSelectedFundIds] = useState(new Set()); // empty = all funds
  const [showFundPicker, setShowFundPicker] = useState(false);
  const fundBtnRef = useRef(null);

  // Quick range buttons
  const setRange = (from, to) => { setDateFrom(from); setDateTo(to); };

  const quickRanges = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const monthStr = `${year}-${String(month).padStart(2, "0")}-01`;
    const lastMonthStart = month === 1
      ? `${year - 1}-12-01`
      : `${year}-${String(month - 1).padStart(2, "0")}-01`;
    const lastMonthEnd = new Date(year, month - 1, 0);
    const lastMonthEndStr = lastMonthEnd.toISOString().slice(0, 10);

    // Prior fiscal year
    const prevFyStartYear = parseInt(fiscal.yearStart.slice(0, 4)) - 1;
    const prevFyStart = `${prevFyStartYear}-${fiscal.yearStart.slice(5)}`;
    const prevFyEnd = new Date(new Date(fiscal.yearStart + "T12:00:00").getTime() - 86400000);
    const prevFyEndStr = prevFyEnd.toISOString().slice(0, 10);
    const prevFyLabel = fiscalStart === 1
      ? `CY ${prevFyStartYear}`
      : `FY ${prevFyStartYear}/${String(prevFyStartYear + 1).slice(2)}`;

    return [
      { label: "This Month", from: monthStr, to: now.toISOString().slice(0, 10) },
      { label: "Last Month", from: lastMonthStart, to: lastMonthEndStr },
      { label: fiscal.label, from: fiscal.yearStart, to: now.toISOString().slice(0, 10) },
      { label: prevFyLabel, from: prevFyStart, to: prevFyEndStr },
      { label: `CY ${year}`, from: `${year}-01-01`, to: now.toISOString().slice(0, 10) },
      { label: `CY ${year - 1}`, from: `${year - 1}-01-01`, to: `${year - 1}-12-31` },
    ];
  }, [fiscal]);

  // Fetch gifts for date range (cursor-paginated by id)
  const loadGifts = useCallback(async () => {
    setLoading(true);
    try {
      let allGifts = [];
      let lastId = null;
      const pageSize = 1000;
      while (true) {
        let query = supabase
          .from("gifts")
          .select("id, amount, gift_date, payment_method, fund_id, funds (id, name, code)")
          .gte("gift_date", dateFrom)
          .lte("gift_date", dateTo)
          .order("id", { ascending: true })
          .limit(pageSize);

        if (lastId) query = query.gt("id", lastId);

        const { data, error } = await query;
        if (error) throw error;
        if (!data || data.length === 0) break;
        allGifts = allGifts.concat(data);
        lastId = data[data.length - 1].id;
        if (data.length < pageSize) break;
      }
      setGifts(allGifts);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, dataVersion]);

  useEffect(() => { loadGifts(); }, [loadGifts]);

  // ── Available funds (derived from fetched gifts) ────────────
  const availableFunds = useMemo(() => {
    const map = {};
    gifts.forEach((g) => {
      const fundId = g.fund_id || "undesignated";
      const name = g.funds?.name || "Undesignated";
      if (!map[fundId]) map[fundId] = name;
    });
    return Object.entries(map)
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [gifts]);

  // ── Filtered gifts (by selected funds) ────────────────────
  const filteredGifts = useMemo(() => {
    if (selectedFundIds.size === 0) return gifts; // no filter = all
    return gifts.filter((g) => {
      const fundId = g.fund_id || "undesignated";
      return selectedFundIds.has(fundId);
    });
  }, [gifts, selectedFundIds]);

  // Fund filter helpers
  const toggleFund = (fundId) => {
    setSelectedFundIds((prev) => {
      const next = new Set(prev);
      if (next.has(fundId)) next.delete(fundId);
      else next.add(fundId);
      return next;
    });
  };

  const clearFundFilter = () => setSelectedFundIds(new Set());

  // ── Computed stats ──────────────────────────────────────────
  const stats = useMemo(() => {
    const totalAmount = filteredGifts.reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0);
    const totalCount = filteredGifts.length;
    const avgGift = totalCount > 0 ? totalAmount / totalCount : 0;

    // By fund
    const fundMap = {};
    filteredGifts.forEach((g) => {
      const name = g.funds?.name || "Undesignated";
      const code = g.funds?.code || "";
      if (!fundMap[name]) fundMap[name] = { name, code, count: 0, total: 0 };
      fundMap[name].count++;
      fundMap[name].total += parseFloat(g.amount) || 0;
    });
    const byFund = Object.values(fundMap).sort((a, b) => b.total - a.total);

    // By payment type
    const typeMap = {};
    filteredGifts.forEach((g) => {
      const key = g.payment_method || "other";
      const label = getTypeLabel(key);
      if (!typeMap[key]) typeMap[key] = { label, count: 0, total: 0 };
      typeMap[key].count++;
      typeMap[key].total += parseFloat(g.amount) || 0;
    });
    const byType = Object.values(typeMap).sort((a, b) => b.total - a.total);

    // By month, grouped by fund
    const monthFundMap = {};
    filteredGifts.forEach((g) => {
      const mk = fmtMonthKey(g.gift_date);
      const fundName = g.funds?.name || "Undesignated";
      if (!monthFundMap[mk]) monthFundMap[mk] = {};
      if (!monthFundMap[mk][fundName]) monthFundMap[mk][fundName] = { count: 0, total: 0 };
      monthFundMap[mk][fundName].count++;
      monthFundMap[mk][fundName].total += parseFloat(g.amount) || 0;
    });

    const months = Object.keys(monthFundMap).sort();
    const byMonth = months.map((mk) => ({
      key: mk,
      label: fmtMonth(mk + "-01"),
      funds: Object.entries(monthFundMap[mk])
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.total - a.total),
      total: Object.values(monthFundMap[mk]).reduce((s, f) => s + f.total, 0),
      count: Object.values(monthFundMap[mk]).reduce((s, f) => s + f.count, 0),
    }));

    return { totalAmount, totalCount, avgGift, byFund, byType, byMonth };
  }, [filteredGifts]);

  // ── Styles ──────────────────────────────────────────────────
  const th = {
    padding: "6px 10px",
    textAlign: "left",
    fontSize: "0.65rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: palette.text3,
    borderBottom: `2px solid ${palette.cardBorder}`,
  };

  const td = {
    padding: "5px 10px",
    fontSize: FONT_SIZES.xs,
    fontFamily: FONT_FAMILY,
    borderBottom: `1px solid ${palette.cardBorder}`,
  };

  return (
    <div>
      {/* ── Date Range Picker ──────────────────────────────── */}
      <Card style={{ marginBottom: 16, padding: 14 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Filter size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
          <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 700, color: palette.text2, fontFamily: FONT_FAMILY }}>
            Date Range:
          </span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{
              padding: "4px 8px", borderRadius: 6, fontSize: FONT_SIZES.xs,
              fontFamily: FONT_FAMILY, border: `1px solid ${palette.inputBorder}`,
              background: palette.inputBg, color: palette.inputColor,
            }}
          />
          <span style={{ fontSize: FONT_SIZES.xs, color: palette.text3 }}>to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{
              padding: "4px 8px", borderRadius: 6, fontSize: FONT_SIZES.xs,
              fontFamily: FONT_FAMILY, border: `1px solid ${palette.inputBorder}`,
              background: palette.inputBg, color: palette.inputColor,
            }}
          />
          <div style={{ width: 1, height: 20, background: palette.cardBorder, margin: "0 4px" }} />
          {quickRanges.map((qr) => (
            <button
              key={qr.label}
              onClick={() => setRange(qr.from, qr.to)}
              style={{
                padding: "4px 10px", borderRadius: 6, fontSize: "0.65rem", fontWeight: 600,
                fontFamily: FONT_FAMILY, cursor: "pointer", border: "none",
                background: dateFrom === qr.from && dateTo === qr.to ? palette.accent1 : `${palette.accent1}10`,
                color: dateFrom === qr.from && dateTo === qr.to ? "#fff" : palette.accent1,
                transition: "all 0.15s",
              }}
            >
              {qr.label}
            </button>
          ))}

          {/* Fund filter */}
          <div style={{ width: 1, height: 20, background: palette.cardBorder, margin: "0 4px" }} />
          <button
            ref={fundBtnRef}
            onClick={() => setShowFundPicker((prev) => !prev)}
            style={{
              padding: "4px 10px", borderRadius: 6, fontSize: "0.65rem", fontWeight: 600,
              fontFamily: FONT_FAMILY, cursor: "pointer",
              border: selectedFundIds.size > 0 ? `1px solid ${palette.accent1}` : "none",
              background: selectedFundIds.size > 0 ? palette.accent1 : `${palette.accent1}10`,
              color: selectedFundIds.size > 0 ? "#fff" : palette.accent1,
              transition: "all 0.15s",
              display: "flex", alignItems: "center", gap: 4,
            }}
          >
            <Filter size={ICON_SIZES.xs} />
            {selectedFundIds.size === 0
              ? "All Funds"
              : `${selectedFundIds.size} Fund${selectedFundIds.size !== 1 ? "s" : ""}`}
          </button>
        </div>
      </Card>

      {/* Fund picker dropdown — rendered outside Card to avoid stacking context */}
      {showFundPicker && (
        <>
          {/* Click-away overlay */}
          <div
            style={{ position: "fixed", inset: 0, zIndex: 998 }}
            onClick={() => setShowFundPicker(false)}
          />
          <div
            style={{
              position: "fixed",
              top: fundBtnRef.current
                ? fundBtnRef.current.getBoundingClientRect().bottom + 4
                : 100,
              left: fundBtnRef.current
                ? fundBtnRef.current.getBoundingClientRect().left
                : 100,
              background: palette.cardBg,
              border: `1px solid ${palette.cardBorder}`,
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
              zIndex: 999,
              minWidth: 220,
              maxHeight: 280,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "8px 12px",
                borderBottom: `1px solid ${palette.cardBorder}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                Filter by Fund
              </span>
              {selectedFundIds.size > 0 && (
                <button
                  onClick={clearFundFilter}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: "0.65rem", color: palette.accent1, fontFamily: FONT_FAMILY,
                    fontWeight: 600, padding: 0,
                  }}
                >
                  Clear All
                </button>
              )}
            </div>
            {/* Fund list */}
            <div style={{ overflowY: "auto", padding: "4px 0" }}>
              {availableFunds.map((fund) => (
                <label
                  key={fund.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 12px",
                    cursor: "pointer",
                    fontSize: FONT_SIZES.xs,
                    fontFamily: FONT_FAMILY,
                    color: palette.text1,
                    background: selectedFundIds.has(fund.id) ? `${palette.accent1}08` : "transparent",
                    transition: "background 0.1s",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedFundIds.has(fund.id)}
                    onChange={() => toggleFund(fund.id)}
                    style={{ cursor: "pointer" }}
                  />
                  {fund.name}
                </label>
              ))}
              {availableFunds.length === 0 && (
                <div style={{ padding: "12px", textAlign: "center", color: palette.text3, fontSize: FONT_SIZES.xs }}>
                  No funds in range
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Active fund filter banner */}
      {selectedFundIds.size > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 14px",
            marginBottom: 12,
            background: `${palette.accent1}08`,
            border: `1px solid ${palette.accent1}30`,
            borderRadius: 8,
            fontSize: FONT_SIZES.xs,
            fontFamily: FONT_FAMILY,
            color: palette.text1,
            flexWrap: "wrap",
          }}
        >
          <Filter size={ICON_SIZES.xs} style={{ color: palette.accent1 }} />
          <span style={{ fontWeight: 600 }}>Filtered to:</span>
          {availableFunds
            .filter((f) => selectedFundIds.has(f.id))
            .map((f) => (
              <span
                key={f.id}
                style={{
                  padding: "2px 8px",
                  borderRadius: 8,
                  background: palette.accent1,
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.65rem",
                }}
              >
                {f.name}
              </span>
            ))}
          <button
            onClick={clearFundFilter}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: palette.accent1, fontFamily: FONT_FAMILY, fontWeight: 600,
              fontSize: FONT_SIZES.xs, padding: 0, marginLeft: 4,
            }}
          >
            Clear Filter
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: palette.text3, fontFamily: FONT_FAMILY }}>
          Loading giving data...
        </div>
      ) : (
        <>
          {/* ═══ SECTION 1: SUMMARY ═══════════════════════════ */}

          {/* KPI Tiles */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
            <Card style={{ textAlign: "center", padding: 16 }}>
              <DollarSign size={ICON_SIZES.md} style={{ color: palette.accent1, marginBottom: 4 }} />
              <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {fmtCurrency(stats.totalAmount)}
              </div>
              <div style={{ fontSize: "0.65rem", fontWeight: 600, color: palette.text3, textTransform: "uppercase", fontFamily: FONT_FAMILY }}>
                Total Given
              </div>
            </Card>
            <Card style={{ textAlign: "center", padding: 16 }}>
              <Calendar size={ICON_SIZES.md} style={{ color: "#10b981", marginBottom: 4 }} />
              <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {stats.totalCount.toLocaleString()}
              </div>
              <div style={{ fontSize: "0.65rem", fontWeight: 600, color: palette.text3, textTransform: "uppercase", fontFamily: FONT_FAMILY }}>
                Total Gifts
              </div>
            </Card>
            <Card style={{ textAlign: "center", padding: 16 }}>
              <TrendingUp size={ICON_SIZES.md} style={{ color: "#f59e0b", marginBottom: 4 }} />
              <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {fmtCurrency(stats.avgGift)}
              </div>
              <div style={{ fontSize: "0.65rem", fontWeight: 600, color: palette.text3, textTransform: "uppercase", fontFamily: FONT_FAMILY }}>
                Average Gift
              </div>
            </Card>
          </div>

          {/* Fund Breakdown */}
          <Card style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1,
              fontFamily: FONT_FAMILY, marginBottom: 12,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <DollarSign size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
              Giving by Fund
            </div>
            {stats.byFund.length === 0 ? (
              <div style={{ textAlign: "center", padding: 20, color: palette.text3, fontSize: FONT_SIZES.xs }}>
                No gifts in this date range.
              </div>
            ) : (
              <>
                {/* Bar chart */}
                <div style={{ marginBottom: 16 }}>
                  {stats.byFund.map((fund) => {
                    const pct = stats.totalAmount > 0 ? (fund.total / stats.totalAmount) * 100 : 0;
                    return (
                      <div key={fund.name} style={{ marginBottom: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY, marginBottom: 3 }}>
                          <span style={{ fontWeight: 600, color: palette.text1 }}>{fund.name}</span>
                          <span style={{ color: palette.text2 }}>
                            {fmtCurrency(fund.total)} ({fund.count} gift{fund.count !== 1 ? "s" : ""}) — {pct.toFixed(1)}%
                          </span>
                        </div>
                        <div style={{ height: 8, background: `${palette.text3}15`, borderRadius: 4, overflow: "hidden" }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: palette.accent1, borderRadius: 4, transition: "width 0.3s" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Summary table */}
                <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
                  <thead>
                    <tr>
                      <th style={th}>Fund</th>
                      <th style={{ ...th, textAlign: "right" }}>Amount</th>
                      <th style={{ ...th, textAlign: "right" }}>Gifts</th>
                      <th style={{ ...th, textAlign: "right" }}>% of Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.byFund.map((fund) => (
                      <tr key={fund.name}>
                        <td style={{ ...td, fontWeight: 600, color: palette.text1 }}>{fund.name}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 600, color: palette.accent1 }}>{fmtCurrencyFull(fund.total)}</td>
                        <td style={{ ...td, textAlign: "right", color: palette.text2 }}>{fund.count}</td>
                        <td style={{ ...td, textAlign: "right", color: palette.text2 }}>
                          {stats.totalAmount > 0 ? ((fund.total / stats.totalAmount) * 100).toFixed(1) + "%" : "—"}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: `2px solid ${palette.cardBorder}` }}>
                      <td style={{ ...td, fontWeight: 700, color: palette.text1 }}>Total</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 700, color: palette.accent1 }}>{fmtCurrencyFull(stats.totalAmount)}</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 700, color: palette.text1 }}>{stats.totalCount}</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>100%</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}
          </Card>

          {/* Payment Type Breakdown */}
          {stats.byType.length > 0 && (
            <Card style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1,
                fontFamily: FONT_FAMILY, marginBottom: 12,
              }}>
                Giving by Payment Type
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
                <thead>
                  <tr>
                    <th style={th}>Type</th>
                    <th style={{ ...th, textAlign: "right" }}>Amount</th>
                    <th style={{ ...th, textAlign: "right" }}>Gifts</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.byType.map((type) => (
                    <tr key={type.label}>
                      <td style={{ ...td, fontWeight: 600, color: palette.text1 }}>{type.label}</td>
                      <td style={{ ...td, textAlign: "right", color: palette.accent1, fontWeight: 600 }}>{fmtCurrencyFull(type.total)}</td>
                      <td style={{ ...td, textAlign: "right", color: palette.text2 }}>{type.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {/* ═══ SECTION 2: DETAIL BY MONTH ═══════════════════ */}
          <Card>
            <div style={{
              fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1,
              fontFamily: FONT_FAMILY, marginBottom: 12,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <Calendar size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
              Monthly Detail by Fund
            </div>

            {stats.byMonth.length === 0 ? (
              <div style={{ textAlign: "center", padding: 20, color: palette.text3, fontSize: FONT_SIZES.xs }}>
                No gifts in this date range.
              </div>
            ) : (
              stats.byMonth.map((month) => (
                <div key={month.key} style={{ marginBottom: 16 }}>
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "8px 10px", background: `${palette.accent1}06`,
                    borderRadius: "6px 6px 0 0", border: `1px solid ${palette.cardBorder}`, borderBottom: "none",
                  }}>
                    <span style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                      {month.label}
                    </span>
                    <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.accent1, fontFamily: FONT_FAMILY }}>
                      {fmtCurrency(month.total)} · {month.count} gift{month.count !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <table style={{
                    width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY,
                    border: `1px solid ${palette.cardBorder}`, borderRadius: "0 0 6px 6px",
                  }}>
                    <tbody>
                      {month.funds.map((fund) => (
                        <tr key={fund.name}>
                          <td style={{ ...td, paddingLeft: 20, color: palette.text1 }}>{fund.name}</td>
                          <td style={{ ...td, textAlign: "right", fontWeight: 600, color: palette.accent1, width: 120 }}>
                            {fmtCurrencyFull(fund.total)}
                          </td>
                          <td style={{ ...td, textAlign: "right", color: palette.text3, width: 60 }}>
                            {fund.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))
            )}
          </Card>
        </>
      )}
    </div>
  );
};
